import React, { useState, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { FlatButton } from 'components/shared/StyledComponents'
import { ColorBaseSkyBlue, ColorBaseWhite, ColorEastBay } from 'assets/styles/variables'

// Template preview imports
import {
    fetchPreviewTemplatePDF,
    fetchUserPreferredTemplate
} from 'businesslayer/networkrequests/templateService'
import { useMinutesTemplates } from 'businesslayer/networkrequests/templates/useMinutesTemplates'
import TemplatePreviewHybrid from 'components/shared/TemplatePreviewHybrid'
import i18n from 'common/util/internationalization'

interface TemplatePopoverButtonProps {
    // No props needed for this component
}

const useStyles = makeStyles(() => ({
    templatePopoverButton: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: '20px',
        marginLeft: '12px',
        height: '40px',
        color: ColorEastBay,
        backgroundColor: ColorBaseWhite,
        border: `1px solid ${ColorEastBay}`,
        padding: '8px 12px 8px 12px',
        '&:focus, &:active': {
            height: '40px',
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseSkyBlue}`,
            borderRadius: '2px'
        },
        '&:hover': {
            height: '40px',
            color: ColorEastBay,
            backgroundColor: '#94949429'
        }
    }
}))

const TemplatePopoverButton: React.FC<TemplatePopoverButtonProps> = () => {
    const classes = useStyles()

    // Template preview state
    const [previewOpen, setPreviewOpen] = useState(false)
    const [imageUrls, setImageUrls] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [previewSelection, setPreviewSelection] = useState<any>({})
    const [templateOption, setTemplateOption] = useState<any>({})
    const [userPreferredTemplateResponse, setUserPreferredTemplateResponse] = useState<any>(
        undefined
    )
    const [lastPreviewedTemplateId, setLastPreviewedTemplateId] = useState<string | null>(null)

    // Pagination state
    const [page, setPage] = useState(1)
    const [perPage] = useState(20) // Fixed per page for now
    const [hasMoreTemplates, setHasMoreTemplates] = useState(false)

    // Template data
    const {
        templateLists,
        isLoading: templatesLoading,
        fetchMinutesTemplates,
        systemTemplate,
        totalTemplates
    } = useMinutesTemplates()

    // Update hasMoreTemplates when templateLists or totalTemplates change
    useEffect(() => {
        if (templateLists !== undefined) {
            const loadedCount = templateLists.length || 0
            const hasMore = totalTemplates > 0 && loadedCount < totalTemplates
            setHasMoreTemplates(hasMore)
        }
    }, [templateLists, totalTemplates])

    // Handle load more templates
    const handleLoadMore = useCallback(async () => {
        const nextPage = page + 1
        setPage(nextPage)
        await fetchMinutesTemplates(nextPage, 'updatedAt', 'desc', perPage)
    }, [page, perPage, fetchMinutesTemplates])

    // Template preview handlers
    const handleGetTemplatePreview = useCallback(
        async (templateId: string) => {
            setPreviewOpen(true)

            // Find the template by ID and set it as preview selection
            const combinedTemplates = systemTemplate
                ? [...systemTemplate, ...templateLists]
                : templateLists
            const selectedTemplate = combinedTemplates.find(
                (template) => template?.id === templateId
            )
            if (selectedTemplate) {
                setPreviewSelection(selectedTemplate)
            }

            try {
                setLoading(true)
                const response = await fetchPreviewTemplatePDF(templateId)
                if (response?.success && response?.images && response?.images?.length > 0) {
                    setImageUrls(response?.images)
                } else {
                    setImageUrls([])
                    console.error('Error fetching template preview:', response?.error)
                }
            } catch (err) {
                setImageUrls([])
                console.error('Error fetching template preview:', err)
            } finally {
                setLoading(false)
            }
        },
        [systemTemplate, templateLists]
    )

    const handleClosePreview = useCallback(() => {
        setPreviewOpen(false)
        setPreviewSelection({}) // Reset preview selection when modal closes
        setTemplateOption({}) // Reset template option when modal closes
        setUserPreferredTemplateResponse(undefined) // Reset user preferred template response when modal closes
        setPage(1) // Reset to first page when modal closes
        setHasMoreTemplates(false) // Reset has more state
        setLastPreviewedTemplateId(null)
    }, [])

    // Effect to open preview when systemTemplate becomes available
    // Removed auto-open effect in favor of a single, data-ready effect below

    // Removed initialization of previewSelection to systemTemplate to avoid flicker before preference resolves

    // Fetch user preferred template when modal is opened
    useEffect(() => {
        if (previewOpen && typeof userPreferredTemplateResponse === 'undefined') {
            const getUserPreferredTemplate = async () => {
                try {
                    const response = await fetchUserPreferredTemplate()
                    setUserPreferredTemplateResponse(response)
                } catch (error) {
                    console.error('Failed to fetch user preferred template:', error)
                    setUserPreferredTemplateResponse(null)
                }
            }
            getUserPreferredTemplate()
        }
    }, [previewOpen, userPreferredTemplateResponse])

    // When modal is open and templates are ready, preview preferred or fall back to system template
    useEffect(() => {
        if (!previewOpen) return
        if (!systemTemplate || (Array.isArray(systemTemplate) && systemTemplate.length === 0))
            return
        // Wait until templates loading completes so we can look up preferred template from list
        if (templatesLoading) return
        // Wait until user preference fetch completes (undefined indicates not fetched yet)
        if (typeof userPreferredTemplateResponse === 'undefined') return

        const combinedTemplates = Array.isArray(systemTemplate)
            ? [...systemTemplate, ...(templateLists || [])]
            : [systemTemplate, ...(templateLists || [])]

        const preferredId = userPreferredTemplateResponse?.preferredTemplate
        let selectedTemplate = Array.isArray(systemTemplate) ? systemTemplate[0] : systemTemplate
        if (preferredId) {
            const preferredTemplate = combinedTemplates.find((t) => t?.id === preferredId)
            if (preferredTemplate) {
                selectedTemplate = preferredTemplate
            }
        }

        if (selectedTemplate?.id) {
            setTemplateOption(selectedTemplate)
            setPreviewSelection(selectedTemplate)
            if (lastPreviewedTemplateId !== selectedTemplate.id) {
                handleGetTemplatePreview(selectedTemplate.id)
                setLastPreviewedTemplateId(selectedTemplate.id)
            }
        }
    }, [
        previewOpen,
        templatesLoading,
        systemTemplate,
        templateLists,
        userPreferredTemplateResponse,
        handleGetTemplatePreview,
        lastPreviewedTemplateId
    ])

    const handleTemplatesButtonClick = useCallback(async () => {
        setPreviewOpen(true)
        setLoading(true) // show loader while we fetch preference/templates and wait for effect to preview
        setLastPreviewedTemplateId(null)
        // Preference will be fetched by the modal-open effect; avoid fetching here to prevent duplicates

        // Ensure templates are loading/loaded
        if (!templateLists || templateLists.length === 0) {
            try {
                await fetchMinutesTemplates(1, 'updatedAt', 'desc', perPage)
            } catch (error) {
                console.error('Failed to fetch templates:', error)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateLists, systemTemplate, fetchMinutesTemplates, handleGetTemplatePreview, perPage])

    return (
        <>
            <FlatButton
                onClick={handleTemplatesButtonClick}
                className={classes.templatePopoverButton}
                disableFocusRipple
                disableRipple
                aria-label="Templates"
                data-analytics="MM-Templates-Button">
                {i18n.t('MINUTES_TEMPLATES_TITLE')}
            </FlatButton>

            {/* Template Preview Hybrid (Modal for Desktop, Drawer for iPad) */}
            <TemplatePreviewHybrid
                open={previewOpen}
                onClose={handleClosePreview}
                imageUrlsData={imageUrls}
                loading={loading}
                onCancel={handleClosePreview}
                templateOption={templateOption}
                setTemplateOption={setTemplateOption}
                systemTemplate={systemTemplate}
                templateOptions={templateLists}
                hasMoreTemplates={hasMoreTemplates}
                handleLoadMore={handleLoadMore}
                isLoading={templatesLoading || typeof userPreferredTemplateResponse === 'undefined'}
                handleGetTemplatePreview={handleGetTemplatePreview}
                previewSelection={previewSelection}
                setPreviewSelection={setPreviewSelection}
                userPreferredTemplateResponse={userPreferredTemplateResponse}
            />
        </>
    )
}

export default TemplatePopoverButton
